import React, { useRef, useEffect, useState, memo, useCallback, useMemo } from "react";
import { ExclamationCircleFilled } from "src/icons";

import { safeGet } from "src/shared/utils";
import AreaChartComponent from "src/shared/components/AreaChart";
import styles from "./index.module.scss";
import { ISinglePin } from "src/shared/types";
import Shimmer from "src/shared/components/CustomisedShimmer";
import ChartFallback from "src/shared/components/ChartFallback";
import { getTimeRoundOffData } from "src/shared/utils/timeUtils";
import { IsTabHidden } from "src/shared/utils/customTab";
import { FEATURE_FLAG, GRANULARITY_MAPPING, TabNames } from "src/shared/config";

interface INumberCardProps {
  header: string;
  chartData: ISinglePin;
  showDetails(id: number, name: string): void;
  selectedTab: typeof TabNames[number];
}

const NumberCard = (props: INumberCardProps) => {
  const { LINE_CHART_FOR_CATEGORY_PAGE } = FEATURE_FLAG;
  const chartData = safeGet(props, "chartData", 0);
  const hasChart = safeGet(props, "chartData.hasChart", false);

  const { bigNumber, vCompare, progress, successful, isPositive, isHidden } = chartData;
  const cardRef = useRef<HTMLDivElement>(null);

  const [cardWidth, setCardWidth] = useState(0);
  const [cardHeight, setCardHeight] = useState(0);

  const barsConfig = useMemo(
    () => [
      { height: "2rem", width: "5.52rem", repeatBar: 1 },
      {
        height: "1.5rem",
        width: "4.16rem",
        repeatBar: 1,
        style: { marginTop: "6%" },
      },
    ],
    []
  );
  const clickCard = useCallback(
    (ev: React.MouseEvent) => {
      if (ev) {
        const metricId = ev.currentTarget.getAttribute("data-item") as unknown as number;
        props.showDetails(metricId, props.header);
      }
    },
    [props.showDetails]
  );

  useEffect(() => {
    setCardWidth(safeGet(cardRef, "current.clientWidth", 0));
    setCardHeight(safeGet(cardRef, "current.clientHeight", 0));
  }, []);

  const parseChartData = useCallback(
    () =>
      getTimeRoundOffData(
        safeGet(props, "chartData.trendChart.data.data[0].values", []),
        GRANULARITY_MAPPING[props.selectedTab]
      ),
    [safeGet(props, "chartData.trendChart.data.data[0].values", [])]
  );
  return (
    <div
      className={styles["number-card"]}
      ref={cardRef}
      onClick={clickCard}
      data-item={safeGet(props, "chartData.id")}
      id={"number-card"}
    >
      <div
        className={`${styles["number-card-text"]} ${
          !LINE_CHART_FOR_CATEGORY_PAGE ? styles["extra-bottom-padding"] : ""
        }`}
      >
        <div className={styles["number-card-header"]}>{props.header}</div>
        {!hasChart ? (
          <div className={styles["ShimmerGroup"]}>
            <Shimmer className={styles["Shimmer"]} barsConfig={barsConfig} />
          </div>
        ) : successful === true ? (
          <>
            <div className={styles["card-chart-number"]}>
              <span>{hasChart && !IsTabHidden(chartData.isHidden, props.selectedTab) ? bigNumber : "N/A"}</span>
              {!IsTabHidden(chartData.isHidden, props.selectedTab) &&
                (safeGet(props, "chartData.isAnomalous", false) || safeGet(props, "chartData.isStaleMetric")) && (
                  <ExclamationCircleFilled className={styles.anomaly} />
                )}
            </div>
            <div
              className={
                vCompare === ""
                  ? styles["card-chart-progress-neutral"]
                  : `${
                      progress == isPositive ? styles["card-chart-progress-up"] : styles["card-chart-progress-down"]
                    } ${progress ? styles["up-arrow"] : styles["down-arrow"]}`
              }
            >
              {hasChart && !IsTabHidden(chartData.isHidden, props.selectedTab) && vCompare}
            </div>

            {LINE_CHART_FOR_CATEGORY_PAGE && (
              <div className={styles["number-card-chart"]}>
                <AreaChartComponent
                  data={parseChartData()}
                  width={cardWidth}
                  height={cardHeight / 4}
                  chartProgress={progress == isPositive}
                  isNeutral={vCompare === ""}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles["number-card-404"]}>
              <ChartFallback message={chartData.message} />
            </div>
            <div className={styles["number-card-chart"]}></div>
          </>
        )}
      </div>
    </div>
  );
};
NumberCard.whyDidYouRender = false;

export default memo(NumberCard);
